<template>
  <div id="paymentWrapper" :class="$x['payment-wrapper']" data-app @click="totalClicks++">
    <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
    <section v-show="!showAddNewPayment && !showEditPayment">
      <ns-alert :class="$x['alert-payments']" type="card" context="error" :title="t.serverErrorTitle" :visible="showPaymentsAlert">
        <template #body><span v-html="sanitizeHTML(t.serverErrorMessage)"></span></template>
      </ns-alert>
      <div :class="$x['choose-payment-icon']">
        <h2 :class="$x['add-payment-title']" class="subtitle-medium-semibold">
          {{ t.addNewPaymentSectionTitle}}
        </h2>
        <p v-show="t.choosePaymentType">{{t.choosePaymentType}}</p>
        <span v-for="c in groupedCreditTypes" :key="c.id">
          <img
            :class="$x['p-icon']"
            :src="getCardIcon(c.id)"
            @click="openAddPaymentAndSetPaymentType(0)"
            :name="c.ofsId"
          />
        </span>
        <span v-for="c in availableSimvTypes" :key="c.id">
          <img
            :class="$x['p-icon']"
            :src="getCardIcon(c.id)"
            @click="openAddPaymentAndSetPaymentType(c.id)"
            :name="c.ofsId"
          />
        </span>
        <span v-for="c in availableCarteBleueTypes" :key="c.id">
          <img
            :class="$x['p-icon']"
            :src="getCardIcon(c.id)"
            @click="openAddPaymentAndSetPaymentType(c.id)"
            :name="c.ofsId"
          />
        </span>
        <span v-for="p in availablePaymentTypes" :key="p.id">
          <img
            :src="getCardIcon(p.id)"
            @click="openAddPaymentAndSetPaymentType(p.id)"
            :name="p.ofsId"
            :class="[$x['p-icon'], {'disabled-icon': p.formName==='emptyPayment' && !saveNonCreditCards}]"
          />
        </span>
      </div>
      <div :class="$x['saved-payments-header']">
        <h2 class="title-small-bold">
          <span>{{ t.savedPaymentMethods }}</span>
        </h2>
        <h3 :class="[$x['add-payment'], canAddPayment() ? $x.lnk : $x['no-lnk']]" @click="openAddPayment('addPaymentLink')">
          <ns-icon icon-name="icon-add" size="icon-lg" /> {{t.add}}
        </h3>
      </div>
      <div v-if="payments.length === 0">{{t.noSavedPayments}}</div>
      <div :class="$x['saved-payment']" v-for="p in payments" :key="p.bePaymentTypeId">
        <div>
          <h3 name="paymentName">{{p.paymentName}}</h3>
          <div :class="$x['cc-info']">
            <img
              :class="$x['p-icon']"
              :src="getCardIcon(p.paymentTypeId)"
            />
            <span v-if="paymentTypeFormName(p.paymentTypeId) !== 'emptyPayment'" name="paymentNumber">{{p.paymentTypeDescription}} {{t.endingIn}} {{getCcLastFour(p.paymentNumber)}}</span>
          </div>
          <div v-if="p.expiring" :class="$x['cc-expiring']" name="expiration">{{t.expiring}} {{p.expiring}}</div>
          <div v-if="p.expired" :class="$x['cc-expired']" name="expired">
            <div v-if="!p.editExpirationDate">
              <ns-icon icon-name="icon-error-lg" style="width: 20px; height: 20px; display: inline-block; margin-right: 5px; vertical-align: bottom;"/>
              <span>{{t.expired}} {{p.expired}} </span>
              <span :class="[$x['update-expired-date'], $x.lnk]" @click="toggleExpirationDateForm(p)">{{t.update}}</span>
            </div>
          </div>
          <div v-if="p.defaultPayment" :class="$x['cc-default']" name="defaultPmt">
            <ns-icon icon-name="icon-checkmark" style="width: 20px; height: 20px; display: inline-block; margin-right: 5px"/>
            <span>{{t.defaultPayment}}</span>
          </div>
          <div v-if="p.editExpirationDate" :class="$x['expiration-date-form-wrapper']">
            <ns-expire-date-form :class="$x['expirationDateForm']" :translations="t" @input="updateExpirationDate(p,$event)"></ns-expire-date-form>
            <button :class="$x['close-expiration-form']" name="closeExpirationForm" @click="toggleExpirationDateForm(p)">
              <ns-icon icon-name="icon-close" style="width: 20px; height: 20px; display: inline-block; margin-right: 5px; vertical-align: bottom;" />
            </button>
          </div>
          <div v-if="paymentTypeFormName(p.paymentTypeId) === 'emptyPayment'">
            <p v-html="sanitizeHTML(p.paymentMethodText)"></p>
          </div>
        </div>
        <div>
          <v-menu left light content-class="pop-menu">
            <template v-slot:activator="{ on }">
              <!-- It seems that the activator clicks are not captured by the payment-wrapper @click event
                   so we update the totalClicks here when the menu is clicked-->
              <button :class="$x['action-button']" name="editMenu" v-on="on" @click="totalClicks++">
                <ns-icon icon-name="icon-menu-dots"/>
              </button>
            </template>
            <v-list>
              <v-list-tile v-if="p.canEdit == true" @click="openEditPayment(p)" name="editPayment">
                <v-list-tile-avatar>
                  <ns-icon icon-name="icon-edit"/>
                </v-list-tile-avatar>
                <v-list-tile-title>{{t.edit}}</v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="openDeleteConfirmation(p.bePaymentTypeId)" name="deletePayment">
                <v-list-tile-avatar>
                  <ns-icon icon-name="icon-trashcan"/>
                </v-list-tile-avatar>
                <v-list-tile-title>{{t.delete}}</v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="doUpdateDefaultPayment(p)" name="updateDefault">
                <v-list-tile-avatar>
                  <ns-icon icon-name="icon-checkmark"/>
                </v-list-tile-avatar>
                <v-list-tile-title>{{t.setAsDefault}}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div :class="[$x['add-payment'], $x.btn]">
        <button :disabled="!canAddPayment()" class="hit-outline fluid" @click="openAddPayment('addPaymentButton')">+ {{t.addNewPaymentMethod}}</button>
      </div>
    </section>

    <section id="editPaymentSection" v-show="showEditPayment">
      <ns-alert :class="$x.alert" type="card" context="error" :title="t.serverErrorTitle" :visible="showAddPaymentAlert">
        <template #body><span v-html="sanitizeHTML(t.serverErrorMessage)"></span></template>
      </ns-alert>
       <h2 class="subtitle-medium-semibold" :class="$x['edit-payment-title']">
          <span>{{ t.editPaymentSectionTitle }}</span>
            <button @click="cleanupAddEditPayment()">
              <ns-icon icon-name="icon-close-x"/>
            </button>
        </h2>
        <ns-credit-card-form
          ref="editCreditCardForm"
          v-model="currentEditedPayment"
          v-if="isCreditCardPaymentType(selectedPaymentType)"
          style="max-width: 500px"
          edit-mode
          :class="$x['credit-card-form']"
          :translations="t"
          :available-payments="formCreditTypes"
          :prevent-jcb-bin="true"
        />
        <div :class="$x['border-top']" v-if="withAddress">
          <ns-address-form
            v-model="addressFormData"
            ref="editAddressForm"
            :lang="language"
            :translations="t.address"
            :editing="editingBillingAddress"
            @editing="setEditingBillingAddress"
            @address-valid="setAddressFormValid"
          ></ns-address-form>
        </div>
        <div v-show="!editingBillingAddress">
          <div :class="$x['border-top']">
            <v-form ref="editPaymentCheckboxForm">
              <v-checkbox v-model="agreeToSave" disabled checked color="#7123D9">
                <template slot="label">
                  {{ t.agreeToSavePaymentType }}
                </template>
              </v-checkbox>
              <v-checkbox v-if="!hideDefaultCheckbox()" v-model="setAsDefault" color="#7123D9">
                <template slot="label">{{ t.setAsDefault }}</template>
              </v-checkbox>
            </v-form>
            <p v-if="enableStoredCredentials" :class="[$x['tac'], $x['message']]">
              {{ t.agreeToTermsOfUseMessage }} <a :href="termsOfUseLnk" :class="[$x.lnk]" target="_blank">{{t.termsOfUse}}</a>
            </p>
            <button class="alert-ghost" type="button" @click="openDeleteConfirmation(currentEditedPayment.bePaymentTypeId)" name="openDeleteConfirmation"><ns-icon icon-name="icon-trashcan"/>{{t.deletePaymentMethod}}</button>
          </div>
          <div :class="$x['border-top']">
            <ns-alert :class="[$x.alert, $x.mobile]" type="card" context="error" :title="t.serverErrorTitle" :visible="showAddPaymentAlert">
              <template #body><span v-html="sanitizeHTML(t.serverErrorMessage)"></span></template>
            </ns-alert>
            <button class="hit-solid fluid form-save" name="saveEditPayment" @click="doSavePayment(currentEditedPayment)">{{ t.save }}</button>
            <button class="hit-outline fluid" name="cancelEditPayment" @click="cleanupAddEditPayment()">{{ t.cancel }}</button>
          </div>
        </div>
    </section>

    <section id="addPaymentSection" v-show="showAddNewPayment && !showEditPayment">
      <ns-alert :class="$x.alert" type="card" context="error" :title="t.serverErrorTitle" :visible="showAddPaymentAlert">
        <template #body><span v-html="sanitizeHTML(t.serverErrorMessage)"></span></template>
      </ns-alert>
      <div :class="$x.contents">
        <div :class="$x.left">
          <h2 class="subtitle-medium-semibold" :class="$x['add-payment-title']">
            {{ t.addNewPaymentSectionTitle }}
          </h2>
          <v-radio-group v-model="selectedPaymentType">
            <!-- this is a hack because v-radio is garbage and doesn't work -->
            <div v-if="groupedCreditTypes.length > 0" class="radio-option">
              <v-radio
                name="payment-type-cc"
                class="subtitle-small-semibold"
                :value=0
                color="#7123D9"
              ></v-radio>
              <span class="label">{{t.creditOption}}</span>
            </div>
            <div v-if="groupedCreditTypes.length > 0" :class="$x['card-icons']">
              <img
                :class="$x['p-icon']"
                v-for="c in groupedCreditTypes"
                :key="c.id"
                :src="getCardIcon(c.id)"
                @click="selectedPaymentType = 0"
              />
            </div>
            <div v-for="p in availableSimvTypes" :key="p.id">
              <div class="radio-option">
                <v-radio
                  :value="p.id"
                  :name="'payment-type-'+p.id"
                  color="#7123D9"
                  :disabled="p.formName==='emptyPayment' && !saveNonCreditCards"
                ></v-radio>
                <span class="label">{{p.localizedName}}</span>
              </div>
              <div :class="$x['card-icons']">
                <img :class="$x['p-icon']" :src="getCardIcon(p.id)" @click="selectedPaymentType = p.id"/>
              </div>
            </div>
            <div v-for="p in availableCarteBleueTypes" :key="p.id">
              <div class="radio-option" @click="selectedPaymentType = p.id">
                <v-radio
                  :value="p.id"
                  :name="'payment-type-' + p.id"
                  color="#7123D9"
                ></v-radio>
                <span class="label">{{ p.localizedName }}</span>
              </div>
              <div :class="$x['card-icons']">
                <img :class="$x['p-icon']" :src="getCardIcon(p.id)" @click="selectedPaymentType = p.id"/>
              </div>
            </div>
            <div v-for="p in availablePaymentTypes" :key="p.id">
              <div class="radio-option">
                <v-radio
                  :value="p.id"
                  :name="'payment-type-'+p.id"
                  color="#7123D9"
                  :disabled="p.formName==='emptyPayment' && !saveNonCreditCards"
                ></v-radio>
                <span class="label">{{p.localizedName}}</span>
              </div>
              <div :class="$x['card-icons']">
                <img :class="$x['p-icon']" :src="getCardIcon(p.id)" @click="selectedPaymentType = p.id"/>
              </div>
            </div>
          </v-radio-group>
        </div>
        <div :class="$x.right">
          <ns-sepa-form
            ref="sepaForm"
            v-if="selectedPaymentType == 54"
            v-model="formData.cardNumber"
            :class="$x['credit-card-form']"
            :name="accountHolderName"
            :translations="t"
            syle="max-width: 500px"
          ></ns-sepa-form>
          <ns-cedyna-form
            ref="cedynaForm"
            v-if="selectedPaymentType == 98"
            v-model="formData"
            :class="$x['credit-card-form']"
            :payment-type-id="formData.paymentTypeId"
            :translations="t"
            :available-payments="availableCedynaTypes"
            style="max-width: 500px"
          ></ns-cedyna-form>
          <ns-credit-card-form
            ref="addCreditCardForm"
            v-model="formData"
            v-if="isCreditCardPaymentType(selectedPaymentType)"
            style="max-width: 500px"
            :class="$x['credit-card-form']"
            :translations="t"
            :available-payments="formCreditTypes"
            :prevent-jcb-bin="true"
          />
          <div id="otherPaymentInfo" v-if="paymentTypeFormName(selectedPaymentType) === 'emptyPayment'">
            <h2 class="subtitle-medium-semibold">{{selectedPaymentTypeLocalizedName}}</h2>
            <img :class="$x['p-icon-large']" :src="getCardIcon(selectedPaymentType)"/>
            <p v-html="sanitizeHTML(paymentMethodsInfo(selectedPaymentType))"></p>
          </div>
          <div :class="$x['border-top']" v-if="withAddress">
            <ns-address-form
              v-model="addressFormData"
              ref="addAddressForm"
              :lang="language"
              :translations="t.address"
              :editing="editingBillingAddress"
              @editing="setEditingBillingAddress"
              @address-valid="setAddressFormValid"
            ></ns-address-form>
          </div>
          <div v-show="!editingBillingAddress">
            <div :class="$x['border-top']">
              <v-form ref="addPaymentCheckboxForm">
                <v-checkbox v-model="agreeToSave" name="agreeToSave" disabled checked color="#7123D9">
                  <template slot="label">
                    {{ t.agreeToSavePaymentType }}
                  </template>
                </v-checkbox>
                <v-checkbox
                  v-if="!hideDefaultCheckbox()"
                  v-model="setAsDefault"
                  name="setAsDefault"
                  color="#7123D9"
                >
                  <template slot="label">{{ t.setAsDefault }}</template>
                </v-checkbox>
              </v-form>
              <p v-if="enableStoredCredentials" :class="[$x['tac'], $x['message']]">
                {{ t.agreeToTermsOfUseMessage }} <a :href="termsOfUseLnk" :class="[$x.lnk]" target="_blank">{{t.termsOfUse}}</a>
              </p>
            </div>
            <div :class="$x['border-top']">
              <ns-alert :class="[$x.alert, $x.mobile]" type="card" context="error" :title="t.serverErrorTitle" :visible="showAddPaymentAlert">
                <template #body><span v-html="sanitizeHTML(t.serverErrorMessage)"></span></template>
              </ns-alert>
              <button class="hit-solid fluid form-save" @click="doSavePayment(formData)" name="saveAddPayment" :disabled="savePaymentProcessing">{{ t.save }}</button>
              <button class="hit-outline fluid" name="cancelAddPayment" v-on:click="cleanupAddEditPayment()">{{ t.cancel }}</button>
            </div>
          </div>
        </div>
      </div>
    </section>
    <div>
      <v-dialog v-model="deleteConfirm" content-class="delete-confirmation">
        <v-card>
          <v-card-title class="title-small-bold">{{t.confirmDeletePayment}}</v-card-title>
          <v-card-actions>
            <button class="inverse-solid short" @click="deleteConfirm = false" name="cancelDelete">{{ t.cancel }}</button>
            <button class="alert-solid short" @click="doDeletePayment()" name="confirmDelete">{{ t.delete }}</button>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { VList, VListTile, VListTileTitle, VListTileAvatar, VRadioGroup, VRadio, VCheckbox, VMenu, VDialog, VCard, VCardTitle, VCardActions } from "vuetify/lib";
import { events } from '@nuskin/ns-util';
import { NsAddressForm } from '@nuskin/smart-common-components';
import { NsIcon, NsCreditCardForm, NsAlert, PaymentType, NsCedynaForm, NsSepaForm, NsExpireDateForm} from "@nuskin/design-components";
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import 'vuetify/dist/vuetify.min.css';
import PAYMENT_MIXIN from './paymentMixin';

const PAYMENTERROR = 'paymentError';

export default {
  name: "NsPaymentManager",
  components: {
    NsIcon,
    NsExpireDateForm,
    NsCreditCardForm,
    NsCedynaForm,
    NsSepaForm,
    VMenu,
    VList,
    VListTile,
    VListTileAvatar,
    VListTileTitle,
    VRadioGroup,
    VRadio,
    VCheckbox,
    VDialog,
    VCard,
    VCardTitle,
    VCardActions,
    NsAlert,
    NsAddressForm
  },
  mixins: [PAYMENT_MIXIN],
  props: {
    creditOnly: {
      type: Boolean,
      default() {
        return false;
      }
    }
  },
  data() {
    return {
      addMethod: null,
      agreeToSave: true,
      currentEditedPayment: {},
      deleteConfirm: false,
      storedCredentialsAgreementModal: false,
      deletePaymentId: '',
      formData: {},
      addressFormData: null,
      editingBillingAddress: false,
      addressFormValid: true,
      setAsDefault: false,
      showAddNewPayment: false,
      showAddPaymentAlert: false,
      showEditPayment: false,
      showPaymentsAlert: false,
      savePaymentProcessing: false,
      showPaymentsNotAvailable: false
    };
  },
  computed: {
    selectedPaymentTypeLocalizedName: function() {
      let paymentType;
      if (this.selectedPaymentType !== 0) {
        paymentType = this.availablePaymentTypes.find(p => this.selectedPaymentType === p.id);
      }
      return paymentType ? paymentType.localizedName : "";
    }
  },
  watch: {
    selectedPaymentType(newValue, oldValue) {
      this.setFormCreditTypes(newValue);
      this.withAddress = this.paymentTypeIsWithAddress(newValue);
      this.addressFormData = this.getBillingAddress(this.showAddNewPayment ? this.defaultShippingAddress : null);
      this.setEditingBillingAddress(this.withAddress && this.editingBillingAddress);

      if (!this.samePaymentType(newValue, oldValue)) {
        this.resetForms();
        this.formData = {};
      }
    }
  },
  methods: {
    paymentMethodsInfo: function (paymentTypeId) {
      let info;
      let paymentType = this.additionalPaymentMethodsInfo.find(p => paymentTypeId === parseInt(p.paymentTypeId));
      if(typeof paymentType !== "undefined" && 'paymentMethodText' in paymentType){
        info =  paymentType.paymentMethodText
      }
      return info ;
    },

    openAddPayment(addMethod) {
      if (this.canAddPayment()) {
        this.addMethod = addMethod;
        this.clearAlerts();
        this.showAddNewPayment = true;
        this.agreeToSave = true;
        this.selectedPaymentType = this.getFirstAvailablePaymentType();
        setTimeout((scope) => {
          document.getElementById('addPaymentSection').scrollIntoView();

          if (scope.withAddress) {
            scope.addressFormData = this.getBillingAddress(scope.defaultShippingAddress);
          }
        }, 125, this);
      }
    },

    openAddPaymentAndSetPaymentType(id, method = 'addPaymentIcon') {
      if(!(this.paymentTypeFormName(id) ==='emptyPayment' && !this.saveNonCreditCards)){
        this.openAddPayment(method);
        this.selectedPaymentType = id;
      }
    },

    async updateExpirationDate(p, event) {
      if (event.expYear && event.expMonth) {
        p.expYear = event.expYear;
        p.expMonth = event.expMonth;
        p = await this.editPayment(p);
        this.setExtraFieldsOnPayment(p.bePaymentTypeId);
        this.$forceUpdate();
      }
    },

    toggleExpirationDateForm(p){
      if(p){
        p.editExpirationDate = !p.editExpirationDate;
        this.$forceUpdate();
      }
    },

    openEditPayment(payment) {
      this.clearAlerts();
      this.withAddress = this.paymentTypeIsWithAddress(payment.paymentTypeId);
      this.showEditPayment = true;
      this.currentEditedPayment = this.getPaymentData(payment);
      this.addressFormData = this.getBillingAddress(payment);
      this.agreeToSave = true;
      this.setAsDefault = payment.defaultPayment;
      this.agreeToSave = false;
      setTimeout((scope) => {
        document.getElementById('editPaymentSection').scrollIntoView();
        scope.$refs.editCreditCardForm.validate();
      }, 125, this);
    },

    setEditingBillingAddress(editingAddress) {
      this.editingBillingAddress = editingAddress;
    },

    setAddressFormValid(isValid) {
      this.addressFormValid = isValid;
    },

    openDeleteConfirmation(paymentId) {
      this.deletePaymentId = paymentId;
      this.deleteConfirm = true;
    },

    async doUpdateDefaultPayment(payment) {
      let defaultStatus = payment.defaultPayment;

      try {
        this.updateDefaultPayment(payment, 'defaultMenuOption');
        this.clearAlerts();
      } catch (err) {
        payment.defaultPayment = defaultStatus;
        this.showPaymentsAlert = true;
        events.tag(PAYMENTERROR, {
            context: this.context,
            errorCategory: 'Failed to set default payment' + err
        });
      }
    },

    async doSavePayment(p) {
      if (!this.withAddress ||
              (this.showAddNewPayment && this.$refs.addAddressForm.validate()) ||
              (this.showEditPayment && this.$refs.editAddressForm.validate())) {
        this.savePaymentProcessing = true;
        // The save button is always enabled. Validate form before continuing.
        let isFormValid;
        if (this.paymentTypeFormName(this.selectedPaymentType) === 'emptyPayment') {
          isFormValid = true;
          // Set default values for non credit card type to save
          p = {
            paymentTypeId: this.selectedPaymentType,
            cardHolderName: this.selectedPaymentTypeLocalizedName
          };
        } else if (this.showAddNewPayment && !this.showEditPayment) {
          switch (this.selectedPaymentType) {
            case PaymentType.CEDN:
              isFormValid = this.$refs.cedynaForm.validate();
              break;
            case PaymentType.SDD:
              isFormValid = this.$refs.sepaForm.validate();
              p.paymentTypeId = this.selectedPaymentType,
              p.cardHolderName = this.accountHolderName;
              break;
            default:
              isFormValid = this.$refs.addCreditCardForm.validate();
          }
        } else {
          if (this.selectedPaymentType === PaymentType.CBLU) {
            isFormValid = this.$refs.editCarteBleueForm.validate();
          } else {
            isFormValid = this.$refs.editCreditCardForm.validate() && this.$refs.editPaymentCheckboxForm.validate();
          }
        }

        if (isFormValid && (!this.withAddress || this.addressFormValid)) {
          let paymentData = {
            ...this.getPaymentData(p, this.setAsDefault, false),
            ...this.getBillingAddress(this.addressFormData, false),
          };

          if (p.bePaymentTypeId) {
            paymentData.bePaymentTypeId = p.bePaymentTypeId;
            try {
              const editMethod = this.showEditPayment ? 'editPaymentCheckbox' : this.showAddNewPayment ? 'addPaymentCheckbox' : 'defaultMenuOption';
              await this.editPayment(paymentData, editMethod);
              this.cleanupAddEditPayment(paymentData);
              this.clearAlerts();
            } catch (err) {
              this.showAddPaymentAlert = true;
              events.tag(PAYMENTERROR, {
                context: this.context,
                errorCategory: 'Failed to edit payment: ' + err
              });
            }
          } else {
            try {
              await this.addPayment(paymentData, this.addMethod ? this.addMethod : 'firstPayment');
              this.cleanupAddEditPayment(paymentData);
              this.clearAlerts();
            } catch (err) {
              this.showAddPaymentAlert = true;
              events.tag(PAYMENTERROR, {
                context: this.context,
                errorCategory: 'Failed to add payment: ' + err
              });
            }
          }
        } else {
          this.savePaymentProcessing = false;
        }
      } else {
        this.editingBillingAddress = true;
      }
    },

    async doDeletePayment() {
      this.deleteConfirm = false;
      // To call the paymentservice we need a loggedin user and the payment must be saved

      const deleted = await this.deletePayment(this.deletePaymentId, this.showEditPayment);
      if (deleted) {
        this.clearAlerts();
        if (this.showEditPayment) {
            this.cleanupAddEditPayment();
        }
      } else {
        this.showEditPayment ? this.showAddPaymentAlert = true : this.showPaymentsAlert = true;
      }
    },

    cleanupAddEditPayment(payment) {
      if (payment) {
        this.setExtraFieldsOnPayment(payment.bePaymentTypeId);
        if (payment.defaultPayment) {
          this.updateDefaultPaymentOrder();
        }
      }
      // Cleanup
      this.showAddNewPayment = false;
      this.showEditPayment = false;
      this.setAsDefault = false;
      this.resetForms();
      this.currentEditedPayment = {};
      this.formData = {};
      this.addressFormData = null;
      this.agreeToSave = false;
      this.savePaymentProcessing = false;
      let paymentWrapper = document.getElementById('paymentWrapper');
      if (paymentWrapper) {
          window.scrollTo(0, paymentWrapper.offsetTop - 60); // 60px accounts for the title displayed by the consuming component
      }
    },

    clearAlerts() {
      this.showPaymentsAlert = false;
      this.showAddPaymentAlert = false;
    }
  },

  mounted() {
    this.initMixin();
    this.subscribeToPageChange();
    this.getAllPaymentInfo()
      .then(([pmtTypesOk, pmtsOk]) => {
        if (!pmtTypesOk) {
          this.showPaymentsAlert = true;
          this.showAddPaymentAlert = true;
        }
        if (!pmtsOk) {
          this.showPaymentsAlert = true;
        }
        if (pmtTypesOk && pmtsOk) {
          if (this.payments.length === 0) {
              this.openAddPaymentAndSetPaymentType(this.getFirstAvailablePaymentType(), 'noPayments');
          } else {
              this.updateDefaultPaymentOrder();
          }
        }
      });
  }
};
</script>

<style lang="scss" module="$x">
@use "./paymentManager.module.scss";
//TODO a temp fix for a weird issue. removing it start throwing error while unit test.
.dummy {
  color: blue;
}
</style>

<style lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/gridStyles";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

@mixin grayscale() {
  filter: grayscale(1);
  opacity: .5;
  cursor: initial;
  pointer-events: none;
}

[class^="NsPaymentManager_"] {
  .title-small-bold {
    @include global.title-small-bold();
  }

  a:not(.v-list__tile__title) {
    color: purple;
    text-decoration: none;
    font-weight: bold;
  }
  a.v-list__tile {
    align-content: center;
    color: inherit;
    display: flex;
    font-size: 16px;
    font-weight: 400;
    height: 48px;
    margin: 0;
    padding: 0 16px;
    position: relative;
    text-decoration: none;
    -webkit-transition: background .3s cubic-bezier(.25,.8,.5,1);
    transition: background .3s cubic-bezier(.25,.8,.5,1);
    text-transform: none;
  }

  .v-select .v-input__control .v-input__slot {
    border: none;
    padding: 0;

    .v-select__slot {
      flex-flow: column nowrap !important;

      .v-input__append-inner {
        bottom: 7px;
        right: 5px;
        top: auto;
        margin: 0;
        z-index: 100;
        position: absolute;
      }
      .v-select__selections {
        position: relative;

        .v-select__selection--comma {
          margin: 0;
          position: absolute;
          top: 50%;
          left: 20px;
          transform: translateY(-50%);
          z-index: 100;
        }

        input {
          border: 1px solid map.get(colors.$baseColors, "dryink");

          &:focus {
            box-shadow: 0 0 5px 2px rgba(113, 35, 217, 0.2);
          }
        }
      }
      label.primary--text {
        color: map.get(colors.$baseColors, "blackhole") !important;
        font-size: 16px;
        font-family: "proxima-nova", sans-serif;
      }
      svg {
        color: map.get(colors.$baseColors, "blackhole") !important;
      }
    }
  }

  .v-menu__content.menuable__content__active.pop-menu {
    min-width: 300px !important;
    border: 3px solid map.get(colors.$baseColors, "dryink") !important;
    overflow: hidden;
  }

  .v-menu__content {
    z-index: 101 !important;
  }

  .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
    align-items: center;

    label {
      margin-bottom: 0;
    }
  }

  .v-label {
    font-family: map.get(typographyVariables.$typography, "font-family-primary");
  }

  .v-radio--is-disabled {
    .v-label {
      color: map.get(colors.$baseColors, "eyore");
    }
  }

  .radio-option {
    display: flex;
    justify-content: flex-start;
    cursor: pointer;
    .label {
      margin-left: -22px;
    }
  }

  .v-input--checkbox {
    .v-icon {
      font-size: 30px;
    }

    .v-messages {
      min-height: 0;
    }
  }

  .title {
    @extend .title;
  }

  .title-small-bold {
    @extend .title-small-bold;
  }

  .v-menu__content.pop-menu .v-list__tile__title, .v-menu__content.pop-menu .v-list__tile__avatar {
    color: map.get(colors.$baseColors, "shells");
  }

  .v-input--selection-controls__input {
    width: 30px;
    height: 30px;
  }

  .v-radio--is-disabled + .card-icons img {
    @include grayscale();
  }
}

[class^="NsCvvForm__"] {
  .v-text-field {
    padding-top: 0;

    &.v-input--is-focused {
      .v-input__slot:before,
      .v-input__slot:after {
        border-width: 0 !important;
        border-color: transparent;
      }
    }

    .v-label--active {
      transform: none;
    }

    input {
      padding: 16px 15px;
    }
  }
}


[class^="NsExpireDateForm__"] {
  display: inline-flex;
  .v-text-field {
    padding-top: 0;

    &.v-input--is-focused {
      .v-input__slot:before,
      .v-input__slot:after {
        border-width: 0 !important;
        border-color: transparent;
      }
    }

    .v-label--active {
      transform: none;
    }

    input {
      padding: 16px 15px;
    }
  }
}

.delete-confirmation {
  .title-small-bold {
    margin-bottom: 22px;
    justify-content: center;
  }

  .v-card__actions {
    justify-content: flex-end;

    button:first-child {
      margin-right: 20px;

      &:focus {
        outline: none;
      }
    }

    .alert-solid {
      min-width: 175px;
    }

    @media screen and (max-width: gridStyles.$phone) {
      flex-flow: column-reverse;

      button {
        width: 100%;

        &:first-child {
          margin-right: 0;
        }

        &:nth-child(2) {
          margin-bottom: 10px;
        }
      }
    }
  }
}

[class*="__nsAddressForm"] {
  @import "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
}

.no-scroll-x {
  overflow-x: hidden;
}
</style>
